import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import AppLogo from "@/assets/images/favicon.png";

import {
	AssignStatus,
	AssignStatusFilter,
	ListProcessingStatus,
	ListSourceType,
} from "@/enum/list.enum";
import { Spinner } from "@/components/Spinner/Spinner";
import {
	useGetLeanListQuery,
	useGetLeanListsQuery,
	useGetListQuery,
} from "@/pages/Private/redux/list/list.api";
import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useCreateReviewSessionMutation } from "@/pages/Private/redux/reviewSession/reviewSession.api";

import { useAppSelector } from "@/redux/hooks";

import { useSearch } from "@/utils";

import { Order, SortParams } from "@/types/types";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { HelpLauncher } from "@/components/HelpCenter/HelpLauncher";

import { ListDetailHeaderActions } from "../../components/ListDetailHeaderActions";
import { ListDetailStatusGroup } from "../../components/ListDetailStatusGroup";
import { UnassignedTable } from "../../components/UnassignedTable";
import { CompaniesTab } from "../../components/CompaniesTab/CompaniesTab";
import { ListSourceTypeElem } from "../../components/ListSourceTypeElem";
import { ListPickerDropdown } from "../../components/ListPickerDropdown";
import { ListViewSwitcher, ListViewMode } from "../../components/ListViewSwitcher";
import { OpenInReviewModal, OpenInReviewScope } from "../../components/OpenInReviewModal";
import { List } from "../../schema/list";
import { ListPersonSocketProvider } from "../../providers/SocketContext";
import { FeaturesCodes } from "../../../Users/enum/codes";
import { TrackedProfileTab } from "../../../ProfileTracker/components/TrackedProfileTab";
import {
	ASSIGNED_TAB_ASSIGN_STATUS_FILTERS,
	getAssignStatusFiltersForOpenInReview,
} from "./openInReviewAssignStatus";
import { AssignLeadsCoachmark } from "./AssignLeadsCoachmark";
import { Tabs } from "./unassignedTabs";
import {
	filteredLeadCount as filteredCountForView,
	hasActiveViewFilters,
	tabCount,
	totalListCount,
} from "./listDetailCounts";
import { ListStatusTabStrip } from "./ListStatusTabStrip";

export const Unassigned: FunctionComponent = () => {
	const { t } = useTranslation();
	const { id } = useParams();
	const ts = (key: string) => t(`inbox.${key}`);
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { setSearchValue, query } = useSearch();
	const { meta } = useAppSelector(listSelector);

	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);

	// The list switcher has no sort UI; always show the newest lists first.
	const sorting: SortParams = { sortBy: "createdAt", sortOrder: Order.DESC };
	const [page, setPage] = useState(1);
	const [listData, setListData] = useState<List[]>([]);
	const [listLoading, setListLoading] = useState(false);

	const [unsureCount, setUnsureCount] = useState<number>(0);

	const [isListProcessing, setIsListProcessing] = useState<boolean>(false);

	const { data, isLoading: isLeanListLoading } = useGetLeanListQuery(
		id ? { id: +id, assignStatus: undefined } : { id: 0, assignStatus: undefined },
		{
			pollingInterval: id && isListProcessing ? 30000 : 0,
		}
	);

	useEffect(() => {
		setIsListProcessing(data?.processingStatus === ListProcessingStatus.PROCESSING);
	}, [data?.processingStatus]);

	// Fetch full list data to get trackers for TRACKER source type
	const { data: fullListData } = useGetListQuery(id ? +id : 0, {
		skip: !id || data?.sourceType !== ListSourceType.TRACKER,
	});

	const {
		data: lists,
		isLoading,
		isFetching,
	} = useGetLeanListsQuery({
		page,
		limit: 15,
		searchValue: query,
		sorting,
	});

	useEffect(() => {
		if (lists) {
			setListData((prevData) => {
				const existingIds = new Set(prevData.map((item) => item.id));
				const newItems = lists.data.filter((item) => !existingIds.has(item.id));

				return [...prevData, ...newItems];
			});
			setListLoading(false);
		}
	}, [lists]);

	const handleLoadMore = () => {
		if (!isFetching && page < (lists?.meta?.pageCount || 0)) {
			setTimeout(() => {
				setPage((prevPage) => prevPage + 1);
			}, 500);
		}
	};
	const [currentTab, setCurrentTab] = useState<Tabs>(
		(searchParams.get("tab") as unknown as Tabs) || Tabs.UNASSIGNED
	);

	useEffect(() => {
		if (searchParams.has("tab")) {
			const nextTab = searchParams.get("tab") as Tabs;

			setCurrentTab(nextTab);
		}
	}, [searchParams]);

	useEffect(() => {
		// Backfill a *missing* tab param only. Re-writing it on every searchParams change
		// self-triggered this effect (each setSearchParams produced a new searchParams reference),
		// churning the URL during a tab switch.
		if (searchParams.has("tab")) {
			return;
		}

		const allParameters = Object.fromEntries(searchParams.entries());

		setSearchParams({ ...allParameters, tab: Tabs.UNASSIGNED }, { replace: true });
	}, [searchParams, setSearchParams]);

	const handleTabsChange = useCallback(
		(value: Tabs): void => {
			const allParameters = Object.fromEntries(searchParams.entries());

			setSearchParams({ ...allParameters, tab: value }, { replace: false });
		},
		[searchParams, setSearchParams]
	);

	const tabs = useMemo(() => {
		const baseTabs = [
			{ label: ts("tabs.unassigned"), value: Tabs.UNASSIGNED },
			{ label: ts("tabs.savedForLater"), value: Tabs.SAVED_FOR_LATER },
			{ label: ts("tabs.assigned"), value: Tabs.ASSIGNED },
			{ label: ts("tabs.archived"), value: Tabs.ARCHIVED },
		];

		if (data?.sourceType === ListSourceType.JOB_CHANGE) {
			baseTabs.push({ label: ts("tabs.underReview"), value: Tabs.UNDER_REVIEW });
		}

		if (data?.sourceType === ListSourceType.TRACKER) {
			baseTabs.push({ label: ts("tabs.trackedProfile"), value: Tabs.TRACKED_PROFILE });
		}

		// PREC-4400: the Companies tab shows while the list holds company rows waiting for a bulk
		// Find person. A list switched back to "find people now" keeps it until those companies are
		// searched or removed, so the gate is the count, not the mode.
		if (Number(data?.companiesCount) > 0) {
			baseTabs.push({ label: ts("tabs.companies"), value: Tabs.COMPANIES });
		}

		return baseTabs;
	}, [data?.sourceType, data?.companiesCount, ts]);

	const [viewMode, setViewMode] = useState<ListViewMode>("list");
	const [isReviewModalOpen, setIsReviewModalOpen] = useState<boolean>(false);
	const [selectedItems, setSelectedItems] = useState<number[]>([]);
	const [createReviewSession, { isLoading: isCreatingReviewSession }] =
		useCreateReviewSessionMutation();

	useEffect(() => {
		setSelectedItems([]);
	}, [currentTab]);

	const handleSelectionChange = useCallback((items: number[]) => {
		setSelectedItems(items);
	}, []);

	const tabAssignStatusFilterMap = useMemo<Record<Tabs, AssignStatusFilter[] | null>>(
		() => ({
			[Tabs.UNASSIGNED]: [AssignStatusFilter.DRAFT],
			[Tabs.SAVED_FOR_LATER]: [AssignStatusFilter.SAVED_FOR_LATER],
			[Tabs.ASSIGNED]: ASSIGNED_TAB_ASSIGN_STATUS_FILTERS,
			[Tabs.ARCHIVED]: [AssignStatusFilter.ARCHIVED],
			[Tabs.UNDER_REVIEW]: [AssignStatusFilter.UNDER_REVIEW],
			[Tabs.TRACKED_PROFILE]: [AssignStatusFilter.TRACKED_PROFILE],
			// PREC-4400: company rows are not an assignStatus bucket; the Companies tab has its own query.
			[Tabs.COMPANIES]: null,
		}),
		[]
	);

	const listTotalCount = useMemo(() => totalListCount(data), [data]);
	const currentTabCount = useMemo(() => tabCount(data, currentTab), [data, currentTab]);

	const filteredLeadCount = useMemo(
		() =>
			filteredCountForView({
				isFiltered: hasActiveViewFilters(searchParams),
				itemCount: meta?.itemCount,
				tab: currentTab,
				tabCount: currentTabCount,
			}),
		[currentTab, currentTabCount, meta?.itemCount, searchParams]
	);

	const currentTabLabel = useMemo(
		() => tabs.find((tab) => tab.value === currentTab)?.label ?? "",
		[tabs, currentTab]
	);

	const isTrackerList = data?.sourceType === ListSourceType.TRACKER;
	const showFeedView = isTrackerList;

	const handleSelectListView = useCallback(() => {
		setViewMode("list");
	}, []);

	const handleSelectReviewView = useCallback(() => {
		setIsReviewModalOpen(true);
	}, []);

	// Feed is a plain navigation to the standalone Feed page (PREC-3635 P4) —
	// no confirm modal, the whole list is the selection.
	const handleSelectFeedView = useCallback(() => {
		if (!data?.id) {
			return;
		}

		const params = new URLSearchParams();

		params.set("listIds", JSON.stringify([{ id: data.id, title: data.name ?? "" }]));

		navigate(`/app/feed?${params.toString()}`);
	}, [data?.id, data?.name, navigate]);

	const handleConfirmOpenInReview = useCallback(
		async (scope: OpenInReviewScope) => {
			if (!data?.id) {
				return;
			}

			const params = new URLSearchParams();
			const listIdsValue = JSON.stringify([{ id: data.id, title: data.name ?? "" }]);

			params.set("listIds", listIdsValue);

			if (scope === "tab" || scope === "filtered") {
				const tabAssignStatusFilters = tabAssignStatusFilterMap[currentTab];
				const assignStatusFilters = tabAssignStatusFilters
					? getAssignStatusFiltersForOpenInReview({
							scope,
							searchParams,
							tabAssignStatusFilters,
						})
					: undefined;

				if (assignStatusFilters) {
					params.set("assignStatus", JSON.stringify(assignStatusFilters));
				}
			}

			if (scope === "filtered") {
				const signalFilterStatusParam = searchParams.get("signalFilterStatus");

				if (signalFilterStatusParam) {
					params.set("signalFilterStatus", signalFilterStatusParam);
				}
			}

			if (scope === "selected") {
				if (selectedItems.length === 0) {
					return;
				}

				try {
					const session = await createReviewSession({
						listId: data.id,
						listPersonIds: selectedItems,
					}).unwrap();

					params.set("reviewSessionId", session.id);
				} catch {
					return;
				}
			}

			setIsReviewModalOpen(false);
			window.open(`/app/messages/ready-to-send?${params.toString()}`, "_blank");
		},
		[
			data?.id,
			data?.name,
			currentTab,
			searchParams,
			tabAssignStatusFilterMap,
			selectedItems,
			createReviewSession,
		]
	);

	const hideReviewAndFeed = currentTab === Tabs.ASSIGNED;

	const viewSwitcherSlot = useMemo(
		() => (
			<ListViewSwitcher
				activeView={viewMode}
				showFeed={showFeedView && !hideReviewAndFeed}
				showReview={!hideReviewAndFeed && !isTrackerList}
				onSelectFeed={handleSelectFeedView}
				onSelectList={handleSelectListView}
				onSelectReview={handleSelectReviewView}
			/>
		),
		[
			viewMode,
			showFeedView,
			isTrackerList,
			hideReviewAndFeed,
			handleSelectFeedView,
			handleSelectListView,
			handleSelectReviewView,
		]
	);

	if (isLeanListLoading) {
		return (
			<div className="flex items-center justify-center w-full h-[50vh]">
				<Spinner color="#4d66f5" />
			</div>
		);
	}

	return (
		<ListPersonSocketProvider>
			<div className="flex flex-col items-stretch w-full">
				{/* Level 1: Overview bar — 48px */}
				<div className="flex items-center gap-2.5 h-12 pb-2 px-2 bg-white border-b border-border-light">
					{data?.specialType !== null ? (
						<div className="flex h-8 items-center bg-inactive-item text-gray-700 rounded-md px-2">
							<img alt="Special Type" className="w-3.5 h-3.5" src={AppLogo} />
						</div>
					) : (
						data?.sourceType && <ListSourceTypeElem size="compact" sourceType={data?.sourceType} />
					)}

					<ListPickerDropdown
						currentPage={page}
						data={listData || []}
						handleFetchMore={() => {
							handleLoadMore();
						}}
						handleSearch={(value: string) => {
							setListLoading(true);
							setListData([]);
							setPage(1);
							setSearchValue(value);
						}}
						handleSelect={(value?: Partial<List>) => {
							if (value && value.id !== undefined) {
								navigate(`/app/lists/detail/${value.id}`);
								setSearchValue("");
								setPage(1);
							}
						}}
						isFetching={isFetching}
						isListLoading={listLoading}
						isLoading={isLoading}
						label={data?.name}
						selectedItem={data?.name || ""}
						totalPages={lists?.meta.pageCount || 0}
					/>

					{/* Status cluster mirrors the List Overview's per-list status (PREC-3641): enrichment
					    toggle, campaign roll-up, and AI-draft queue. Shares the flexible middle region so it
					    shrinks/hides first on narrow viewports, keeping the actions cluster intact. */}
					{data && <ListDetailStatusGroup list={data} />}

					<div className="flex-1" />

					<ListDetailHeaderActions
						id={id || ""}
						isSequencerActive={isSequencerActive}
						list={data}
					/>

					<HelpLauncher />
				</div>

				{/* Level 2: Status tabs — 34px */}
				<ListStatusTabStrip
					currentTab={currentTab}
					list={data}
					tabs={tabs}
					unsureCount={unsureCount}
					onTabChange={handleTabsChange}
				/>

				{/* Content area. Key on the active tab so a switch fully remounts the table and re-runs its
				    row-fetch effect — without it the fetch did not re-fire on a client-side tab switch
				    (the request never went out; only a refresh reloaded the rows). */}
				<div key={currentTab} className="w-full">
					{currentTab === Tabs.UNASSIGNED && data?.sourceType && (
						<UnassignedTable
							assignStatus={AssignStatus.DRAFT}
							listType={data?.sourceType}
							onSelectionChange={handleSelectionChange}
							setUnsureCount={setUnsureCount}
							stickyHeader
							toolbarSlot={viewSwitcherSlot}
						/>
					)}
					{currentTab === Tabs.SAVED_FOR_LATER && data?.sourceType && (
						<UnassignedTable
							assignStatus={AssignStatus.SAVED_FOR_LATER}
							listType={data?.sourceType}
							onSelectionChange={handleSelectionChange}
							stickyHeader
							toolbarSlot={viewSwitcherSlot}
						/>
					)}
					{currentTab === Tabs.ASSIGNED && data?.sourceType && (
						<UnassignedTable
							assignStatus={AssignStatus.ASSIGNED}
							listType={data?.sourceType}
							onSelectionChange={handleSelectionChange}
							stickyHeader
							toolbarSlot={viewSwitcherSlot}
						/>
					)}
					{currentTab === Tabs.ARCHIVED && data?.sourceType && (
						<UnassignedTable
							assignStatus={AssignStatus.ARCHIVED}
							listType={data?.sourceType}
							onSelectionChange={handleSelectionChange}
							stickyHeader
							toolbarSlot={viewSwitcherSlot}
						/>
					)}
					{currentTab === Tabs.UNDER_REVIEW && data?.sourceType && (
						<UnassignedTable
							assignStatus={AssignStatus.UNDER_REVIEW}
							listType={data?.sourceType}
							onSelectionChange={handleSelectionChange}
							stickyHeader
							toolbarSlot={viewSwitcherSlot}
						/>
					)}
					{currentTab === Tabs.TRACKED_PROFILE && data && (
						<TrackedProfileTab
							listId={id}
							trackerOrganizationCount={data.trackerOrganizationCount}
							trackers={fullListData?.trackers}
						/>
					)}
					{currentTab === Tabs.COMPANIES && <CompaniesTab listId={id} />}
				</div>

				<AssignLeadsCoachmark selectedCount={selectedItems.length} />

				<OpenInReviewModal
					counts={{
						selected: selectedItems.length,
						filtered: filteredLeadCount,
						tab: currentTabCount,
						all: listTotalCount,
					}}
					currentTabLabel={currentTabLabel}
					isConfirming={isCreatingReviewSession}
					isOpened={isReviewModalOpen}
					onClose={() => setIsReviewModalOpen(false)}
					onConfirm={handleConfirmOpenInReview}
				/>
			</div>
		</ListPersonSocketProvider>
	);
};
